// InlineEditFieldWithMentions.jsx - Inline editing with mention support
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  ClickAwayListener,
  useTheme,
  alpha,
  Fade,
} from '@mui/material';
import {
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

// Import mention components
import InlineMentionInput from '../../ui/FormFields/InlineMentionInput';
import MentionText from '../../ui/MentionText';

/**
 * InlineEditFieldWithMentions - Inline editing component with @mention support
 */
const InlineEditFieldWithMentions = ({
  value,
  onChange,
  onMentionedUsersChange,
  placeholder = 'Click to edit',
  variant = 'description', // 'description' or 'comment'
  multiline = true,
  rows = 3,
  disabled = false,
  autoFocus = false,
  sx = {},
}) => {
  const theme = useTheme();
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState(value || '');
  const [isHovered, setIsHovered] = useState(false);
  const [mentionedUsers, setMentionedUsers] = useState([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const inputRef = useRef(null);
  const contentRef = useRef(null);
  
  // Update local value when prop changes
  useEffect(() => {
    setLocalValue(value || '');
  }, [value]);
  
  // State to track if content overflows
  const [hasOverflow, setHasOverflow] = useState(false);
  
  // Check for overflow after render
  useEffect(() => {
    const checkOverflow = () => {
      if (contentRef.current && multiline) {
        const element = contentRef.current;
        const isOverflowing = element.scrollHeight > element.clientHeight;
        setHasOverflow(isOverflowing);
      }
    };
    
    // Check immediately and after a short delay (for fonts to load)
    checkOverflow();
    const timer = setTimeout(checkOverflow, 100);
    
    return () => clearTimeout(timer);
  }, [value, multiline, isExpanded]);
  
  // Auto-focus when editing starts
  useEffect(() => {
    if (isEditing && inputRef.current) {
      // Use a small delay to ensure the InlineMentionInput is rendered
      const timer = setTimeout(() => {
        // The inputRef now points directly to the InlineMentionInput's internal ref
        if (inputRef.current) {
          inputRef.current.focus();
          // Set cursor at the end of content
          const selection = window.getSelection();
          const range = document.createRange();
          range.selectNodeContents(inputRef.current);
          range.collapse(false);
          selection.removeAllRanges();
          selection.addRange(range);
        }
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [isEditing]);
  
  // Handle edit start
  const startEdit = () => {
    if (disabled) return;
    setIsEditing(true);
    setMentionedUsers([]);
  };
  
  // Handle save
  const handleSave = () => {
    onChange(localValue);
    if (onMentionedUsersChange && mentionedUsers.length > 0) {
      onMentionedUsersChange(mentionedUsers);
    }
    setIsEditing(false);
  };
  
  // Handle cancel
  const handleCancel = () => {
    setLocalValue(value || '');
    setMentionedUsers([]);
    setIsEditing(false);
  };
  
  // Handle key press
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      handleCancel();
    }
    // Don't save on Enter for multiline fields
  };
  
  // Calculate content height for read more functionality
  const calculateMinHeight = () => {
    return multiline ? `${rows * 24}px` : '40px';
  };
  
  const maxHeight = '200px';

  // Render read-only view
  if (!isEditing) {
    const isEmpty = !value;
    const minHeight = calculateMinHeight();
    
    return (
      <Box
        sx={{
          position: 'relative',
          ...sx,
        }}
      >
        <Box
          ref={contentRef}
          onClick={startEdit}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            cursor: disabled ? 'default' : 'pointer',
            borderRadius: 1,
            transition: 'all 0.2s ease',
            position: 'relative',
            p: 1,
            minHeight: minHeight,
            maxHeight: !isExpanded ? maxHeight : 'none',
            overflow: !isExpanded ? 'hidden' : 'visible',
            bgcolor: isHovered && !disabled ? alpha(theme.palette.primary.main, 0.04) : 'transparent',
            border: `1px solid transparent`,
            '&:hover': !disabled && {
              borderColor: alpha(theme.palette.divider, 0.2),
            },
          }}
        >
          {isEmpty ? (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                fontStyle: 'italic',
              }}
            >
              {placeholder}
            </Typography>
          ) : (
            <Box sx={{ position: 'relative' }}>
              <MentionText text={value} variant="body2" />
              {!isExpanded && multiline && hasOverflow && (
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    height: '30px',
                    background: `linear-gradient(to bottom, transparent, ${theme.palette.background.paper})`,
                    pointerEvents: 'none',
                  }}
                />
              )}
            </Box>
          )}
          
          {/* Edit icon on hover */}
          {isHovered && !disabled && (
            <Fade in>
              <EditIcon
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: 8,
                  fontSize: 16,
                  color: 'text.secondary',
                }}
              />
            </Fade>
          )}
          
        </Box>
        
        {/* Read more/less button */}
        {!isEmpty && multiline && hasOverflow && (
          <Button
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded(!isExpanded);
            }}
            sx={{
              mt: 0.5,
              textTransform: 'none',
              minWidth: 'auto',
              p: 0,
              height: 'auto',
              '&:hover': {
                background: 'transparent',
                textDecoration: 'underline',
              },
            }}
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </Button>
        )}
      </Box>
    );
  }
  
  // Render edit mode
  return (
    <ClickAwayListener onClickAway={() => {
      // Save on click away
      onChange(localValue);
      setIsEditing(false);
    }}>
      <Box sx={{ position: 'relative', ...sx }}>
        <InlineMentionInput
          ref={inputRef}
          value={localValue}
          onChange={(newValue) => {
            // InlineMentionInput passes the text value directly, not an event
            setLocalValue(newValue);
            // Immediately notify parent to enable auto-save
            if (onChange) {
              onChange(newValue);
            }
          }}
          onMentionAdd={(user) => {
            // Track mentioned users when they're added
            setMentionedUsers(prev => [...prev.filter(u => u._id !== user._id), user]);
          }}
          onMentionRemove={(userId) => {
            // Remove user from mentioned users when mention is removed
            setMentionedUsers(prev => prev.filter(u => u._id !== userId));
          }}
          placeholder={placeholder}
          inputProps={{
            style: { 
              minHeight: calculateMinHeight(),
              maxHeight: maxHeight,
            },
            'data-placeholder': placeholder,
          }}
        />
        
      </Box>
    </ClickAwayListener>
  );
};

export default InlineEditFieldWithMentions;